/**
 * 生日選擇 — 年 / 月 / 日 三欄下拉
 *
 * 為什麼不用 <input type="date">:
 *   高端 / 年長客群對手機原生 date picker 不友善(只能點不能打、部分 iOS / LINE
 *   in-app browser 點了不跳輪),且空值時整欄透出米白底,看起來像被鎖住(反灰)。
 *   改三欄下拉:點開即選、語意清楚;對外 value 維持 "YYYY-MM-DD",後端 contract 不動
 *   (dashboard 生日提醒會 parse 此字串,務必補零成定長 10 字、不可送無效日期)。
 *
 * value / onChange 一律是 "YYYY-MM-DD"(或 "" = 未填,生日為選填)。
 */
import { useEffect, useMemo, useState } from "react";

interface Props {
  value: string;            // "YYYY-MM-DD" 或 ""
  onChange: (v: string) => void;
  minYear?: number;         // 最早可選年份,預設 1925(涵蓋長者)
  maxYear?: number;         // 最晚可選年份,預設今年
}

// 指定年月的天數;month 為 "1"~"12",year 可空(空時 2 月以平年 28 天計)
// new Date(y, monthNum, 0) = 該月最後一天 → 自動處理大小月與閏年 2/29
function daysInMonth(year: string, month: string): number {
  if (!month) return 31;
  const y = year ? Number(year) : 2001; // 2001 為平年
  return new Date(y, Number(month), 0).getDate();
}

export default function BirthdaySelect({ value, onChange, minYear = 1925, maxYear }: Props) {
  const topYear = maxYear ?? new Date().getFullYear();
  // 三欄各自獨立 state — 用單一 "YYYY-MM-DD" 推不出「只選了年月、還沒選日」的半填狀態
  const [y, setY] = useState("");
  const [m, setM] = useState("");
  const [d, setD] = useState("");

  // 外部帶入完整生日(getMe 非同步回填)時同步進三欄;
  // 只認長度 >= 10 的完整日期 — 我們自己 emit 的 "" 回波不會覆蓋,半填狀態得以保留
  useEffect(() => {
    if (value && value.length >= 10) {
      const [vy, vm, vd] = value.split("-");
      setY(vy);
      setM(String(Number(vm))); // 去前導 0 對應 option value("06" → "6")
      setD(String(Number(vd)));
    }
  }, [value]);

  const years = useMemo(
    () => Array.from({ length: topYear - minYear + 1 }, (_, i) => topYear - i),
    [topYear, minYear],
  );

  // 三欄齊了才組 "YYYY-MM-DD" 往上送;日若超出當月天數(換月後的 2/31、平年 2/29)清成空
  function push(ny: string, nm: string, nd: string) {
    const safeDay = nd && Number(nd) > daysInMonth(ny, nm) ? "" : nd;
    if (nd !== safeDay) setD(safeDay);
    onChange(
      ny && nm && safeDay ? `${ny}-${nm.padStart(2, "0")}-${safeDay.padStart(2, "0")}` : "",
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2">
      <select
        className="input"
        aria-label="出生年"
        value={y}
        onChange={(e) => {
          setY(e.target.value);
          push(e.target.value, m, d);
        }}
      >
        <option value="">年</option>
        {years.map((yr) => (
          <option key={yr} value={yr}>{yr}</option>
        ))}
      </select>

      <select
        className="input"
        aria-label="出生月"
        value={m}
        onChange={(e) => {
          setM(e.target.value);
          push(y, e.target.value, d);
        }}
      >
        <option value="">月</option>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((mo) => (
          <option key={mo} value={mo}>{mo}</option>
        ))}
      </select>

      <select
        className="input"
        aria-label="出生日"
        value={d}
        onChange={(e) => {
          setD(e.target.value);
          push(y, m, e.target.value);
        }}
      >
        <option value="">日</option>
        {Array.from({ length: daysInMonth(y, m) }, (_, i) => i + 1).map((day) => (
          <option key={day} value={day}>{day}</option>
        ))}
      </select>
    </div>
  );
}
